import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { uploadDocument } from '../services/docSenseService';

/**
 * Lightweight searchable single-select dropdown.
 * Used for Location and Sub-location inside the upload modal.
 */
const SearchableSelect = ({ options = [], value, onChange, placeholder, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => String(o.value) === String(value));

  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSelect = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Trigger */}
      <div
        onClick={handleOpen}
        style={{
          border: `1.5px solid ${open ? '#3f88a5' : '#e2eaf0'}`,
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: '0.9rem',
          color: selected ? '#1a2e38' : '#9ab4bf',
          background: disabled ? '#f7fafc' : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          boxShadow: open ? '0 0 0 3px rgba(63,136,165,0.1)' : 'none',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          userSelect: 'none',
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : placeholder}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {selected && !disabled && (
            <span
              onClick={handleClear}
              style={{ color: '#9ab4bf', fontSize: '0.8rem', cursor: 'pointer', lineHeight: 1, padding: '0 2px' }}
              title="Clear"
            >
              ×
            </span>
          )}
          <i className={`fa fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: '0.7rem', color: '#9ab4bf' }} />
        </div>
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          zIndex: 1200,
          background: 'white',
          border: '1.5px solid #e2eaf0',
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        }}>
          {/* Search input */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f7' }}>
            <div style={{ position: 'relative' }}>
              <i className="fa fa-search" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ab4bf', fontSize: '0.75rem' }} />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                style={{
                  width: '100%',
                  border: '1.5px solid #e2eaf0',
                  borderRadius: 6,
                  padding: '5px 10px 5px 28px',
                  fontSize: '0.85rem',
                  color: '#1a2e38',
                  outline: 'none',
                  background: '#f7fafc',
                }}
              />
            </div>
          </div>

          {/* Options */}
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '12px 14px', fontSize: '0.83rem', color: '#9ab4bf', textAlign: 'center' }}>
                No results
              </div>
            ) : (
              filtered.map(opt => (
                <div
                  key={opt.value}
                  onClick={() => handleSelect(opt)}
                  style={{
                    padding: '9px 14px',
                    fontSize: '0.875rem',
                    color: String(opt.value) === String(value) ? '#3f88a5' : '#1a2e38',
                    fontWeight: String(opt.value) === String(value) ? 600 : 400,
                    background: String(opt.value) === String(value) ? 'rgba(63,136,165,0.07)' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={(e) => { if (String(opt.value) !== String(value)) e.currentTarget.style.background = '#f7fafc'; }}
                  onMouseLeave={(e) => { if (String(opt.value) !== String(value)) e.currentTarget.style.background = 'transparent'; }}
                >
                  {opt.label}
                  {String(opt.value) === String(value) && (
                    <i className="fa fa-check" style={{ color: '#3f88a5', fontSize: '0.75rem' }} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

/**
 * sources shape: [{ id, unitCode, location: { city }, subLocation: [] }]
 */
const UploadDocumentModal = ({ show, onClose, onUploaded, financialYearOptions = [], sources = [] }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [financialYearId, setFinancialYearId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [subLocationId, setSubLocationId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Build options
  const locationOptions = sources.map(s => ({
    label: s.unitCode ? s.unitCode :  + (s.location.city + ' - ' + s.location.state),
    value: s.id,
  }));

  const selectedSource = sources.find(s => String(s.id) === String(locationId));
  const subLocations = selectedSource?.subLocation || [];
  console.log('Sub-locations for selected source:', subLocations);
  const subLocationOptions = subLocations.map(sub => ({
    label: sub.subLocation,
    value: sub.id,
  }));

  const handleLocationChange = (val) => {
    setLocationId(val);
    setSubLocationId(''); // always reset sub when location changes
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) { setError('Please select a valid file (PDF, Excel, or Word)'); return; }
    setSelectedFile(file);
    setError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) { setError('Please select a valid file (PDF, Excel, or Word)'); return; }
    setSelectedFile(file);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) { setError('Please select a file'); return; }
    if (!financialYearId) { setError('Please select a financial year'); return; }
    if (!locationId) { setError('Please select a location'); return; }

    setUploading(true);
    setError(null);
    try {
      const resolvedSubId = subLocations.length > 0 && subLocationId ? subLocationId : null;
      const data = await uploadDocument(selectedFile, financialYearId, locationId, resolvedSubId);

      const locationName = selectedSource?.unitCode || null;
      const subLocationName = subLocations.find(s => String(s.id) === String(subLocationId))?.unitCode
        || subLocations.find(s => String(s.id) === String(subLocationId))?.name
        || null;

      onUploaded({
        id: data?.id || data?.documentId,
        fileName: selectedFile.name,
        financialYearId,
        locationId,
        subLocationId: resolvedSubId,
        locationName,
        subLocationName,
        status: 'PENDING',
        createdAt: new Date().toISOString(),
        ...(data || {}),
      });
      handleReset();
      onClose();
    } catch (err) {
      console.error(err);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setFinancialYearId('');
    setLocationId('');
    setSubLocationId('');
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => { if (!uploading) { handleReset(); onClose(); } };

  const getFileIcon = (file) => {
    if (!file) return null;
    if (file.type === 'application/pdf') return '📄';
    if (file.type.includes('excel') || file.type.includes('spreadsheet')) return '📊';
    return '📝';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Modal show={show} onHide={handleClose} size="md" centered>
      <Modal.Header closeButton style={{ borderBottom: '1.5px solid #e2eaf0', padding: '18px 24px' }}>
        <Modal.Title style={{ color: '#3f88a5', fontWeight: 700, fontSize: '1.05rem' }}>
          <i className="fa fa-upload me-2" />Add New Document
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '24px' }}>

        {/* Financial Year */}
        <div className="mb-3">
          <label style={labelStyle}>Financial Year <Required /></label>
          <SearchableSelect
            options={financialYearOptions}
            value={financialYearId}
            onChange={setFinancialYearId}
            placeholder="Select Financial Year"
            disabled={uploading}
          />
        </div>

        {/* Location */}
        <div className="mb-3">
          <label style={labelStyle}>Location <Required /></label>
          <SearchableSelect
            options={locationOptions}
            value={locationId}
            onChange={handleLocationChange}
            placeholder="Select Location"
            disabled={uploading}
          />
        </div>

        {/* Sub-location — only when selected location has children */}
        {locationId && subLocationOptions.length > 0 && (
          <div className="mb-3">
            <label style={labelStyle}>
              Sub-location
            </label>
            <SearchableSelect
              options={subLocationOptions}
              value={subLocationId}
              onChange={setSubLocationId}
              placeholder="Select Sub-location"
              disabled={uploading}
            />
          </div>
        )}

        {/* File Drop Zone */}
        <div className="mb-3">
          <label style={labelStyle}>Document <Required /></label>

          {!selectedFile ? (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: '2px dashed #3f88a5', borderRadius: 10,
                padding: '32px 20px', textAlign: 'center',
                cursor: 'pointer', backgroundColor: '#f8fbfc', transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8f4f8'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fbfc'}
            >
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>📂</div>
              <div style={{ color: '#3f88a5', fontWeight: 600, fontSize: '0.9rem' }}>
                Drop file here or click to browse
              </div>
              <div style={{ color: '#9ab4bf', fontSize: '0.78rem', marginTop: 4 }}>PDF, Excel, Word supported</div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.xlsx,.xls,.doc,.docx"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                disabled={uploading}
              />
            </div>
          ) : (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              border: '1.5px solid #3f88a5', borderRadius: 10,
              padding: '14px 16px', backgroundColor: '#e8f4f8',
            }}>
              <span style={{ fontSize: '1.5rem' }}>{getFileIcon(selectedFile)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a2e38', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedFile.name}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#6b8794' }}>{formatFileSize(selectedFile.size)}</div>
              </div>
              {!uploading && (
                <button
                  onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  style={{ background: 'none', border: 'none', color: '#9ab4bf', cursor: 'pointer', fontSize: '1.2rem', padding: '0 4px' }}
                >×</button>
              )}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: 'rgba(231,76,60,0.08)', color: '#e74c3c', borderRadius: 8, padding: '10px 14px', fontSize: '0.85rem' }}>
            <i className="fa fa-exclamation-circle me-2" />{error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ borderTop: '1.5px solid #e2eaf0', padding: '14px 24px' }}>
        <Button variant="secondary" onClick={handleClose} disabled={uploading}>Cancel</Button>
        <Button
          onClick={handleUpload}
          disabled={!selectedFile || !financialYearId || !locationId || uploading}
          style={{ backgroundColor: '#3f88a5', border: 'none', minWidth: 120 }}
        >
          {uploading
            ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Uploading...</>
            : <><i className="fa fa-upload me-2" />Upload</>
          }
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const Required = () => <span style={{ color: '#e74c3c' }}>*</span>;
const labelStyle = { fontSize: '0.85rem', fontWeight: 600, color: '#1a2e38', marginBottom: 6, display: 'block' };

export default UploadDocumentModal;